/* eslint-disable react-refresh/only-export-components --
 * Context module: Provider + hook are intentionally co-located.
 */
import React, { createContext, useContext, useMemo } from 'react';
import type { FormTemplateResponse } from '../../services/api/types.gen';

interface FormTemplatesContextValue {
  /** Templates targeting a question, in the order the picker should list them. */
  templatesForQid: (qid: string) => readonly FormTemplateResponse[];
}

const FormTemplatesContext = createContext<FormTemplatesContextValue | null>(
  null
);

// One shared empty list, so a question with no templates answers with the same
// reference every time. A fresh `[]` per call would change the dependency of
// the memo in `useFieldTemplates` on every render, which is the common case:
// most of a form's text questions have no template.
const NO_TEMPLATES: readonly FormTemplateResponse[] = [];

/**
 * Templates the agency has authored for one form question.
 *
 * Deliberately does NOT throw when no provider is mounted, and returns an empty
 * list instead. The text primitives this feeds are rendered in places that have
 * no visit and no agency library behind them: the bespoke layout coverage
 * checker renders sections offscreen, and the layout studio previews them in an
 * iframe. Those must fall back to a plain field rather than error.
 *
 * Only ``exact_format`` templates are returned. A ``general_guidance`` entry is
 * direction on how to write an answer rather than the answer itself, so loading
 * one into the box would file the instruction as the documentation.
 */
export function useFieldTemplates(
  qid: string
): readonly FormTemplateResponse[] {
  const ctx = useContext(FormTemplatesContext);
  const templates = ctx?.templatesForQid(qid) ?? NO_TEMPLATES;
  return useMemo(() => {
    const loadable = templates.filter(
      template => template.kind === 'exact_format'
    );
    // Collapse back to the shared reference so a question whose only templates
    // are guidance is as stable as one with none at all.
    return loadable.length > 0 ? loadable : NO_TEMPLATES;
  }, [templates]);
}

/**
 * Whether this question has any loadable template.
 *
 * For callers that must not render a wrapper around the picker when it would
 * come back empty: an empty element still consumes a gap in a `space-y` stack,
 * which would loosen every compact field that has no template.
 */
export function useHasFieldTemplates(qid: string): boolean {
  return useFieldTemplates(qid).length > 0;
}

export function FormTemplatesProvider({
  templates,
  children,
}: {
  templates: FormTemplateResponse[];
  children: React.ReactNode;
}) {
  // Indexed once per library rather than scanned per field. A form renders many
  // text boxes and each one asks this question on every render, so a linear
  // filter per field would be the library length times the field count on every
  // keystroke.
  const byQid = useMemo(() => {
    const map = new Map<string, FormTemplateResponse[]>();
    for (const template of templates) {
      const existing = map.get(template.qid);
      if (existing) {
        existing.push(template);
      } else {
        map.set(template.qid, [template]);
      }
    }
    // Alphabetical, because the name is all a clinician has to tell two
    // templates apart and a stable order makes the picker learnable.
    for (const list of map.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return map;
  }, [templates]);

  const value = useMemo(
    () => ({
      templatesForQid: (qid: string) => byQid.get(qid) ?? NO_TEMPLATES,
    }),
    [byQid]
  );

  return (
    <FormTemplatesContext.Provider value={value}>
      {children}
    </FormTemplatesContext.Provider>
  );
}
